import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./index.less";

const PADDING = 8;
const HEADER_OFFSET = 8;
const DRAG_SENSITIVE_DISTANCE = 1;

const clamp = (value, min, max) => {
  return Math.max(min, Math.min(value, max));
};

function ContextMenuView(props) {
  const {
    enableTrigger = true,
    triggerSelector = "canvas",
    shouldOpen,
    items = [],
    onClose,
  } = props;

  const rootRef = useRef(null);
  const rightButtonDownRef = useRef(false);
  const rightButtonDownPointRef = useRef({ x: 0, y: 0 });
  const rightButtonDownValidRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [triggerPosition, setTriggerPosition] = useState({ x: 0, y: 0 });
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 });

  const visibleItems = useMemo(() => {
    return items.filter((item) => item?.visible !== false);
  }, [items]);

  const isValidTriggerTarget = useCallback(
    (target) => {
      if (!target?.closest) return false;
      if (!triggerSelector) return true;
      return Boolean(target.closest(triggerSelector));
    },
    [triggerSelector]
  );

  useLayoutEffect(() => {
    if (!visible) return;

    const updatePos = () => {
      const panel = rootRef.current;
      if (!panel) return;
      const panelRect = panel.getBoundingClientRect();
      const maxX = window.innerWidth - panelRect.width - PADDING;
      const maxY = window.innerHeight - panelRect.height - PADDING;
      setPanelPosition({
        x: clamp(triggerPosition.x, PADDING, maxX),
        y: clamp(triggerPosition.y, PADDING + HEADER_OFFSET, maxY),
      });
    };

    updatePos();
  }, [visible, triggerPosition, visibleItems.length]);

  useEffect(() => {
    if (!enableTrigger) {
      setVisible(false);
      rightButtonDownRef.current = false;
      rightButtonDownValidRef.current = false;
      return undefined;
    }

    const handleRightButtonDown = (event) => {
      if (event.button !== 2) return;
      const isValidTarget = isValidTriggerTarget(event.target);
      const canOpen = typeof shouldOpen === "function" ? shouldOpen(event) : true;
      rightButtonDownRef.current = true;
      rightButtonDownValidRef.current = isValidTarget && canOpen;
      rightButtonDownPointRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleRightButtonUp = (event) => {
      if (event.button !== 2) return;
      if (!rightButtonDownRef.current || !rightButtonDownValidRef.current) {
        rightButtonDownRef.current = false;
        rightButtonDownValidRef.current = false;
        return;
      }
      const deltaX = event.clientX - rightButtonDownPointRef.current.x;
      const deltaY = event.clientY - rightButtonDownPointRef.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      rightButtonDownRef.current = false;
      rightButtonDownValidRef.current = false;
      if (distance > DRAG_SENSITIVE_DISTANCE) return;
      event.preventDefault();
      setTriggerPosition({ x: event.clientX, y: event.clientY });
      setVisible(true);
    };

    // prevent native browser context menu while keeping open logic on right-button down
    const handleContextMenu = (event) => {
      if (!isValidTriggerTarget(event.target)) return;
      if (typeof shouldOpen === "function" && !shouldOpen(event)) return;
      event.preventDefault();
    };

    window.addEventListener("mousedown", handleRightButtonDown);
    window.addEventListener("mouseup", handleRightButtonUp);
    window.addEventListener("contextmenu", handleContextMenu);
    return () => {
      window.removeEventListener("mousedown", handleRightButtonDown);
      window.removeEventListener("mouseup", handleRightButtonUp);
      window.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [enableTrigger, isValidTriggerTarget, shouldOpen]);

  useEffect(() => {
    if (!visible) return;

    const handleOutsidePointer = (event) => {
      if (event.button === 2) {
        setVisible(false);
        if (onClose) onClose();
        return;
      }
      const isInside = event.target?.closest
        ? event.target.closest(".contextMenuView")
        : null;
      if (!isInside) {
        setVisible(false);
        if (onClose) onClose();
      }
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setVisible(false);
        if (onClose) onClose();
      }
    };

    window.addEventListener("mousedown", handleOutsidePointer);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("mousedown", handleOutsidePointer);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [visible, onClose]);

  if (!visible || visibleItems.length === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={"contextMenuView"}
      style={{ left: `${panelPosition.x}px`, top: `${panelPosition.y}px` }}
    >
      {visibleItems.map((item) => {
        const disabled = Boolean(item.disabled);
        return (
          <button
            key={item.id}
            className={"contextMenuItem"}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (typeof item.onClick === "function") {
                item.onClick();
              }
              setVisible(false);
              if (onClose) {
                onClose();
              }
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default ContextMenuView;

